'use client';

import {
	BigNum,
	PositionDirection,
	ZERO,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	calculatePotentialProfit,
} from '@velocity-exchange/common';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import React from 'react';
import { compareVelocityProps } from 'src/utils/compareProps';
import usePreviewOrder from './hooks/usePreviewOrder';
import ValueDisplay from '../ValueDisplay';

type EstimatedProfitProps = {
	marketId: MarketId;
	currentPositionSize: BigNum;
	currentPositionEntryPrice: BigNum;
	currentPositionDirection: 'long' | 'short';
	side: 'buy' | 'sell';
	limitExitSize: BigNum;
	limitExitPrice: BigNum;
	takerFeeBps?: number;
	customLabel?: string;
	showPlusSign?: boolean;
	isMarketOrder?: boolean;
	className?: string;
};

const EstimatedProfit = (props: EstimatedProfitProps) => {
	const previewOrderParams = usePreviewOrder(props.marketId);

	const { estimatedProfit } = calculatePotentialProfit({
		currentPositionSize: props.currentPositionSize,
		currentPositionDirection: ENUM_UTILS.toObj(
			props.currentPositionDirection
		) as PositionDirection,
		tradeDirection:
			props.side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT,
		exitBaseSize: props.limitExitSize,
		exitPrice: props.isMarketOrder
			? previewOrderParams.priceImpact?.entryPrice
				? BigNum.from(
						previewOrderParams.priceImpact.entryPrice,
						PRICE_PRECISION_EXP
					)
				: props.limitExitPrice
			: props.limitExitPrice,
		currentPositionEntryPrice: props.currentPositionEntryPrice,
		isMarketOrder: props.isMarketOrder,
		takerFeeBps: props.takerFeeBps || 0,
	});

	if (!props.currentPositionSize || !estimatedProfit) {
		return null;
	}

	return (
		<ValueDisplay.Default
			label="Estimated Profit"
			value={`${
				estimatedProfit.gte(ZERO) ? (props.showPlusSign ? '+' : '') : '-'
			}${formatText(estimatedProfit.abs(), PRESETS.usd)}`}
			valueClassName={
				estimatedProfit.gte(ZERO) ? 'text-positive-green' : 'text-negative-red'
			}
		/>
	);
};

export default React.memo(EstimatedProfit, compareVelocityProps);
